import { useMemo, useRef, useState } from 'react';
import { Empty } from 'antd';
import { TradevestPricePoint } from '@mygate/common';

interface Props {
  points: TradevestPricePoint[];
  currency?: string;
  height?: number;
}

const WIDTH = 720;
const PAD = { top: 16, right: 16, bottom: 28, left: 64 };
const POSITIVE = '#3f8600';
const NEGATIVE = '#cf1322';

function formatPrice(value: number, currency?: string): string {
  const symbol = currency === 'EUR' ? '€' : currency ? `${currency} ` : '';
  return `${symbol}${value.toFixed(2)}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/**
 * Dependency-free SVG line chart for an instrument price series (MYG-397). Auto-scales to the series
 * min/max, colours by overall direction, and shows a hover crosshair with a date/price tooltip.
 */
export function PriceLineChart({ points, currency, height = 260 }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const geom = useMemo(() => {
    if (points.length === 0) return null;
    const closes = points.map(p => p.close);
    const min = Math.min(...closes);
    const max = Math.max(...closes);
    const span = max - min;
    const flat = span === 0;
    const innerW = WIDTH - PAD.left - PAD.right;
    const innerH = height - PAD.top - PAD.bottom;
    const xAt = (i: number) => PAD.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
    // Flat / single-point series (min === max): center the line instead of gluing it to the bottom axis.
    const yAt = (v: number) => (flat ? PAD.top + innerH / 2 : PAD.top + innerH - ((v - min) / span) * innerH);
    const line = points.map((p, i) => `${xAt(i).toFixed(1)},${yAt(p.close).toFixed(1)}`).join(' ');
    const area = `${PAD.left},${(PAD.top + innerH).toFixed(1)} ${line} ${xAt(points.length - 1).toFixed(1)},${(
      PAD.top + innerH
    ).toFixed(1)}`;
    const up = points[points.length - 1].close >= points[0].close;
    // Collapse the 3 y-labels to 1 when flat so the same price isn't printed three times.
    const yLabels = flat ? [max] : [max, (min + max) / 2, min];
    return { min, max, flat, xAt, yAt, line, area, up, innerH, yLabels, lastIdx: points.length - 1 };
  }, [points, height]);

  if (!geom) {
    return <Empty description="No chart data" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const color = geom.up ? POSITIVE : NEGATIVE;

  const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * WIDTH;
    const innerW = WIDTH - PAD.left - PAD.right;
    const ratio = Math.min(1, Math.max(0, (relX - PAD.left) / innerW));
    setHoverIdx(Math.round(ratio * (points.length - 1)));
  };

  const hover = hoverIdx != null && points[hoverIdx] ? { point: points[hoverIdx], idx: hoverIdx } : null;
  const tooltipX = hover ? geom.xAt(hover.idx) : 0;
  const tooltipOnRight = tooltipX < WIDTH / 2;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${WIDTH} ${height}`}
      width="100%"
      height={height}
      onMouseMove={handleMove}
      onMouseLeave={() => setHoverIdx(null)}
      style={{ display: 'block', cursor: 'crosshair', userSelect: 'none' }}
    >
      <defs>
        <linearGradient id="tv-price-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.18} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>

      {/* horizontal gridlines + y labels (max / mid / min, or a single label for a flat series) */}
      {geom.yLabels.map((v, i) => {
        const y = geom.yAt(v);
        return (
          <g key={i}>
            <line x1={PAD.left} y1={y} x2={WIDTH - PAD.right} y2={y} stroke="#f0f0f0" strokeWidth={1} />
            <text x={PAD.left - 8} y={y + 4} textAnchor="end" fontSize={11} fill="#8c8c8c">
              {formatPrice(v, currency)}
            </text>
          </g>
        );
      })}

      <polygon points={geom.area} fill="url(#tv-price-fill)" />
      <polyline points={geom.line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
      {/* static marker for the latest price — also keeps a single-point series visible */}
      <circle cx={geom.xAt(geom.lastIdx)} cy={geom.yAt(points[geom.lastIdx].close)} r={3} fill={color} />

      {/* x labels: first + last date */}
      <text x={PAD.left} y={height - 8} textAnchor="start" fontSize={11} fill="#8c8c8c">
        {formatDate(points[0].t)}
      </text>
      <text x={WIDTH - PAD.right} y={height - 8} textAnchor="end" fontSize={11} fill="#8c8c8c">
        {formatDate(points[points.length - 1].t)}
      </text>

      {/* hover crosshair + tooltip */}
      {hover && (
        <g>
          <line
            x1={tooltipX}
            y1={PAD.top}
            x2={tooltipX}
            y2={height - PAD.bottom}
            stroke="#bfbfbf"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
          <circle cx={tooltipX} cy={geom.yAt(hover.point.close)} r={4} fill={color} stroke="#fff" strokeWidth={1.5} />
          <g transform={`translate(${tooltipOnRight ? tooltipX + 8 : tooltipX - 132}, ${PAD.top + 4})`}>
            <rect width={124} height={38} rx={4} fill="#1f1f1f" opacity={0.92} />
            <text x={8} y={16} fontSize={11} fill="#fff" fontWeight="bold">
              {formatPrice(hover.point.close, currency)}
            </text>
            <text x={8} y={30} fontSize={10} fill="#bfbfbf">
              {formatDate(hover.point.t)}
            </text>
          </g>
        </g>
      )}
    </svg>
  );
}
